import "./WorkerWeekStrip.css";

/**
 * 한 사람의 한 주를 한 줄에 담는다.
 *
 * "평일 09:00–19:00 / 주말 09:00–15:00" 같은 글줄로는 요일마다 길이가 다른 경우를 적을 수
 * 없고, 두 줄로 접혀 표의 다른 줄까지 밀어낸다. 일곱 칸을 나란히 두면 나오는 날과 안 나오는
 * 날, 그리고 그날의 길이가 한눈에 갈린다 — 카드의 세로 격자와 같은 사실을 가로로 편 것이다.
 *
 * 창은 6시부터 22시까지다. 하루 전체를 그리면 아무도 일하지 않는 새벽이 막대보다 넓은 자리를
 * 차지한다.
 */
const days = ["월", "화", "수", "목", "금", "토", "일"];
const windowStart = 6 * 60, windowEnd = 22 * 60;
const clock = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const offset = (minutes: number) => `${Math.max(0, Math.min(100, (minutes - windowStart) / (windowEnd - windowStart) * 100))}%`;

export type WeekSchedule = {
  workDays: string[];
  workStartMinutes?: number;
  weekdayMinutes: number;
  weekendMinutes: number;
  breakMinutes?: number;
};

export function WorkerWeekStrip({ schedule }: { schedule: WeekSchedule }) {
  const start = schedule.workStartMinutes ?? 540;
  const span = (day: string) => {
    if (!schedule.workDays.includes(day)) return null;
    const minutes = ["토", "일"].includes(day) ? schedule.weekendMinutes : schedule.weekdayMinutes;
    return minutes > 0 ? { from: start, to: start + minutes + (schedule.breakMinutes ?? 0) } : null;
  };
  if (!schedule.workDays.length) return <span className="worker-week-none">—</span>;
  /* 2026-10-19 밤 지윤: 요일 글자는 뺀다 — 칸 일곱 개가 늘 월→일 같은 자리에 서므로 자리가 곧 요일이다.
     읽는 기계에는 나오는 요일을 한 줄로 알려 준다. */
  return <div className="worker-week" role="img" aria-label={`근무 ${days.filter(day => span(day)).join("·") || "없음"}`}>
    {days.map(day => {
      const range = span(day);
      /* 브라우저 기본 툴팁은 1초쯤 얹고 있어야 뜨는데, 궁금해서 얹은 사람에게는 아무 일도
         안 일어난 것으로 보인다. 쪽지를 직접 그린다. 쉬는 날에는 쪽지를 띄우지 않는다(빈 칸이 이미 말한다 — 2026-10-19 밤 지윤). */
      return <div className="worker-week-day" key={day} data-time={range ? `${day} ${clock(range.from)}–${clock(range.to)}` : undefined}>
        {/* 칸 안에서 몇 시부터 몇 시까지인지를 그리지 않는다 — 14px 안에서는 채움의 위치가
            읽히지 않아 장식이 된다. 표가 답하는 것은 "그날 나오는가"이고, 몇 시인지는 얹으면
            나온다. 안 나오는 날도 칸을 비워 둔 채 남긴다 — 자리가 사라지면 요일이 밀려 다른
            사람과 같은 자리에서 견줄 수 없다. */}
        <div className={`worker-week-cell${range ? " on" : ""}`} />
      </div>;
    })}
  </div>;
}
